import { createAsyncThunk } from '@reduxjs/toolkit';
import { CompletedActivityPayloadType, UserRank } from 'interfaces';
import { HTTP_STATS_CODE, PLATFORMS, ROUTINE } from 'constants/general';
import { isApiRateLimited, isRateLimitError } from 'utils/api-rate-limit.util';
import focusBearApi from 'services/axios-config';
import {
  REQUEST_ERROR_MESSAGE_KEY,
  TRANSIENT_ERROR_PAYLOAD
} from 'constants/error.constant';
import { toastRequestError } from 'utils/api-error.util';
import { increment } from 'utils/helpers';
import Endpoints from 'constants/endpoints';
import {
  updateLastCompletedActivityLog,
  updateWeeklySummaryLoading
} from '../user/slice';
import {
  getWeeklyStats,
  getUserDetailsCurrentActivityProps
} from '../user/extra';
import {
  changeAreLibraryActivitiesLoading,
  changeIsSettingLoaded,
  changeIsSettingLoading,
  updateDevice,
  updateIsActivityLibraryUpdating,
  updateIsDeviceRegistering,
  updateIsFetchingDeviceSettings,
  updateIsPlatformCoursesSyncing,
  updateIsRoutineSummaryFetching,
  updateIsSavingRoutineCompletedActivity,
  updateIsSearchingDevice,
  updateIsUpdatingDeviceSettings,
  updatePreviousActivityLibrary,
  updateShowSkipActivityModal
} from './slice';
import { setHasDataLoadFailed } from '../general/slice';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { SettingsType } from 'interfaces/setting.interface';
import { ActivityType } from 'interfaces/common.interface';
import { LEADER_BOARD } from 'constants/table';
import { RootState } from 'store';
import { Mode } from 'enums';
import { ROUTES } from 'constants/routes';
import { captureException } from 'services/posthog.service';
import { LocalDeviceSettings } from 'interfaces/initial-states.interface';
import { isCurrentPath } from 'utils/routes.util';
import {
  denormalizeSettingsTypeForApi,
  normalizeSettingsType
} from 'utils/daysOfWeek.util';
import { isApiDataValid, isRecord } from 'utils/api/api-data-validity.util';

export const initUserSettings = createAsyncThunk(
  'setting/init_user_settings',
  async (_, { dispatch }) => {
    try {
      dispatch(updateWeeklySummaryLoading(true));
      const { data } = await focusBearApi.get(Endpoints.USER_SETTINGS);
      if (!isApiDataValid(data, isRecord)) {
        dispatch(setHasDataLoadFailed(true));
        return;
      }
      dispatch(getUserDetailsCurrentActivityProps());
      dispatch(getWeeklyStats());
      return data as SettingsType;
    } catch (error) {
      captureException(error);
      // The thunk swallows the error, so the `.rejected` reducer never fires.
      // Surface the shared refresh affordance here instead.
      dispatch(setHasDataLoadFailed(true));
    }
  }
);

export const getUserSettings = createAsyncThunk(
  'setting/get_user_settings',
  async (_, { dispatch }) => {
    try {
      dispatch(changeIsSettingLoading(true));
      const { data } = await focusBearApi.get(Endpoints.USER_SETTINGS);
      if (!isApiDataValid(data, isRecord)) {
        dispatch(setHasDataLoadFailed(true));
        return;
      }
      return data as SettingsType;
    } catch (error) {
      captureException(error);
      // The thunk swallows the error, so the `.rejected` reducer never fires.
      // Surface the shared refresh affordance here instead.
      dispatch(setHasDataLoadFailed(true));
    }
  }
);

export const saveUserSettings = createAsyncThunk(
  'setting/save_user_settings',
  async (payload: SettingsType, { dispatch, getState, rejectWithValue }) => {
    const { device_id, mode, customRoutine } = (getState() as RootState)
      .setting;
    const isOnboardingRoute = isCurrentPath(ROUTES.ONBOARD_FLOW);
    const is_onboarding =
      (mode === Mode.CUSTOM && customRoutine === ROUTINE.SUGGESTION) ||
      isOnboardingRoute;
    try {
      dispatch(updateIsSavingRoutineCompletedActivity(true));
      const response = await focusBearApi.put(
        Endpoints.USER_SETTINGS,
        denormalizeSettingsTypeForApi(normalizeSettingsType(payload)),
        {
          params: { device_id, is_onboarding }
        }
      );

      const status = response?.status ?? 0;
      const isSuccess = status >= HTTP_STATS_CODE.SUCCESS && status < 300;

      if (isSuccess) {
        dispatch(changeIsSettingLoaded(false));
        toast.success(t('settings_saved'));
      } else {
        toast.error(t(REQUEST_ERROR_MESSAGE_KEY.SETTINGS_SAVE_FAILED));
        return rejectWithValue(null);
      }
    } catch (error) {
      if (isRateLimitError(error)) {
        return rejectWithValue({ status: HTTP_STATS_CODE.TOO_MANY_REQUEST });
      }
      const isTransient = toastRequestError(error, {
        fallbackKey: REQUEST_ERROR_MESSAGE_KEY.SETTINGS_SAVE_FAILED
      });
      captureException(error);
      return rejectWithValue(isTransient ? TRANSIENT_ERROR_PAYLOAD : null);
    } finally {
      dispatch(updateIsSavingRoutineCompletedActivity(false));
    }
  },
  {
    condition: (_, { getState }) => {
      const { areSettingsSaving } = (getState() as RootState).setting;
      const { apiRateLimitedUntil } = (getState() as RootState).general;

      if (areSettingsSaving) {
        return false;
      }

      if (isApiRateLimited(apiRateLimitedUntil)) {
        return false;
      }

      return true;
    }
  }
);

export const getLibraryActivities = createAsyncThunk(
  'setting/get_library_activities',
  async (_, { dispatch }) => {
    try {
      dispatch(changeAreLibraryActivitiesLoading(true));
      const { data } = await focusBearApi.get<ActivityType[]>(
        Endpoints.ACTIVITY_LIBRARY
      );
      return data ?? [];
    } catch (error) {
      captureException(error);
    }
  }
);

export const updateLibraryActivities = createAsyncThunk(
  'setting/update_library_activities',
  async (libraryActivities: ActivityType[], { dispatch }) => {
    try {
      dispatch(updateIsActivityLibraryUpdating(true));
      const { status } = await focusBearApi.put(
        Endpoints.ACTIVITY_LIBRARY,
        libraryActivities
      );
      dispatch(updateIsActivityLibraryUpdating(false));
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('settings_saved'));
        dispatch(updatePreviousActivityLibrary(libraryActivities));
      } else {
        toast.error(t(REQUEST_ERROR_MESSAGE_KEY.SETTINGS_SAVE_FAILED));
      }
    } catch (error) {
      dispatch(updateIsActivityLibraryUpdating(false));
      toastRequestError(error, {
        fallbackKey: REQUEST_ERROR_MESSAGE_KEY.SETTINGS_SAVE_FAILED
      });
      captureException(error);
    }
  }
);

export const saveRoutineCompletedActivity = createAsyncThunk(
  'setting/save_routine_completed_activity',
  async (activity: CompletedActivityPayloadType, { dispatch }) => {
    try {
      dispatch(updateIsSavingRoutineCompletedActivity(true));
      const { status, data } = await focusBearApi.post(
        Endpoints.COMPLETED_ACTIVITY,
        activity
      );
      if (status === HTTP_STATS_CODE.CREATED && data) {
        dispatch(updateLastCompletedActivityLog(data));
      }
      dispatch(updateIsSavingRoutineCompletedActivity(false));
    } catch (error) {
      toast.error(t('errors.couldnt_update_your_settings'));
      dispatch(updateIsSavingRoutineCompletedActivity(false));
      captureException(error);
    }
  }
);

export const getRoutineSummary = createAsyncThunk(
  'setting/get_routine_summary',
  async (sequence_id: string, { dispatch }) => {
    try {
      dispatch(updateIsRoutineSummaryFetching(true));
      const { data } = await focusBearApi.get(
        Endpoints.COMPLETED_ACTIVITY_SEQUENCE_STATS.replace(
          '{activity_sequence_id}',
          sequence_id
        )
      );
      return data?.daily_durations_minutes ?? [];
    } catch (error) {
      captureException(error);
    }
  }
);

export const registerUserDevice = createAsyncThunk(
  'setting/register_user_device',
  async (
    payload: { operating_system: string; device_id?: string },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsDeviceRegistering(true));
      const { data } = await focusBearApi.post(Endpoints.DEVICE, payload);
      if (data) {
        dispatch(updateDevice(data));
        toast.success(t('routine_player.device_registration_successful'));
      } else {
        toast.error(t('routine_player.device_registration_faild'));
      }
    } catch (error) {
      t('routine_player.device_registration_faild');
      captureException(error);
    } finally {
      dispatch(updateIsDeviceRegistering(false));
    }
  }
);

export const saveSkipRoutineCompletedActivity = createAsyncThunk(
  'setting/save_skip_routine_completed_activity',
  async (activity: CompletedActivityPayloadType, { dispatch }) => {
    try {
      dispatch(updateIsSavingRoutineCompletedActivity(true));
      const { status, data } = await focusBearApi.post(
        Endpoints.COMPLETED_ACTIVITY_SKIP,
        activity
      );
      if (status === HTTP_STATS_CODE.CREATED && data) {
        dispatch(updateLastCompletedActivityLog(data));
      }
      dispatch(updateIsSavingRoutineCompletedActivity(false));
      dispatch(updateShowSkipActivityModal(false));
    } catch (error) {
      toast.error(t('errors.couldnt_update_your_settings'));
      dispatch(updateIsSavingRoutineCompletedActivity(false));
      dispatch(updateShowSkipActivityModal(false));
      captureException(error);
    }
  }
);

export const getUserStatsLeaderBoard = createAsyncThunk(
  'setting/get_user_stats_leader_board',
  async (queries: { streak_type: string; limit: number }, { dispatch }) => {
    try {
      let params = {};
      if (queries.streak_type) {
        params = { ...params, streak_type: queries.streak_type };
      }
      if (queries.limit) {
        params = { ...params, limit: queries.limit };
      } else {
        params = { ...params, limit: LEADER_BOARD.STAT_LIMIT };
      }
      const { data } = await focusBearApi.get(
        Endpoints.USER_STATS_LEADER_BOARD,
        {
          params
        }
      );
      if (data.users_rankings) {
        const rank = parseInt(data.user_rank?.rank);
        const ranks = data?.users_rankings ?? [];

        let rankings: UserRank[] = [];
        const isUserInTheList = ranks.some(
          (user: UserRank) => user.id === data.user_rank?.id
        );
        if (!isUserInTheList && !isNaN(rank)) {
          rankings = ranks.map((user: UserRank) => {
            if (parseInt(user.rank) >= rank) {
              const newRank = increment(parseInt(user.rank));
              return { ...user, rank: newRank.toString() };
            } else {
              return user;
            }
          });
          rankings.splice(rankings.length - 1, 1, data.user_rank);
        } else {
          rankings = ranks;
        }
        return { user_rank: data.user_rank, users_rankings: rankings };
      } else {
        return null;
      }
    } catch (error) {
      dispatch(updateWeeklySummaryLoading(false));
      captureException(error);
    }
  }
);

export const syncPlatformCourses = createAsyncThunk(
  'setting/syncPlatformCourses',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsPlatformCoursesSyncing(true));
      await focusBearApi.post(Endpoints.SYNC_PLATFORM_COURSES, {
        platform: PLATFORMS.WEB
      });
      dispatch(updateIsPlatformCoursesSyncing(false));
    } catch (error) {
      dispatch(updateIsPlatformCoursesSyncing(false));
      captureException(error);
    }
  }
);

export const searchDevice = createAsyncThunk(
  'setting/search_device',
  async (
    params: { operating_system?: string; device_id?: string },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsSearchingDevice(true));
      const { data } = await focusBearApi.get(`${Endpoints.DEVICE}/search`, {
        params
      });
      return data;
    } catch (error) {
      captureException(error);
      return null;
    } finally {
      dispatch(updateIsSearchingDevice(false));
    }
  }
);

export const getLocalDeviceSettings = createAsyncThunk(
  'setting/update_local_device_settings',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsFetchingDeviceSettings(true));
      const { data } = await focusBearApi.get(
        Endpoints.USER_LOCAL_DEVICE_SETTINGS
      );
      return data;
    } catch (error) {
      captureException(error);
    }
  }
);

export const updateLocalDeviceSettings = createAsyncThunk(
  'setting/update_local_device_settings',
  async (payload: LocalDeviceSettings, { dispatch }) => {
    try {
      const { data } = await focusBearApi.put(
        Endpoints.USER_LOCAL_DEVICE_SETTINGS,
        payload
      );
      dispatch(updateIsUpdatingDeviceSettings(false));
      return data;
    } catch (error) {
      dispatch(updateIsUpdatingDeviceSettings(false));
      captureException(error);
    }
  }
);
